import {
  getBuildingsWithCalendars,
  getCalendarEvents,
  createCalendarEvent,
  deleteCalendarEvent,
} from '@/services/api/calendar/calendar'
import { CALENDAR_COLORS } from '@/constants'

export default {
  namespaced: true,
  state: {
    buildings: [],
    events: [],
    selectedDistrictId: null,
    loadingBuildings: false,
    loadingEvents: false,
  },
  mutations: {
    SET_BUILDINGS(state, buildings) {
      state.buildings = buildings
    },
    SET_EVENTS(state, events) {
      state.events = events
    },
    SET_LOADING_BUILDINGS(state, loading) {
      state.loadingBuildings = loading
    },
    SET_LOADING_EVENTS(state, loading) {
      state.loadingEvents = loading
    },
    SET_SELECTED_DISTRICT(state, districtId) {
      state.selectedDistrictId = districtId
    },

    SET_BUILDING_ENABLED(state, { buildingId, enabled }) {
      const building = state.buildings.find((b) => b.id === buildingId)
      if (building) {
        building.enabled = enabled
      }
    },
  },
  actions: {
    setSelectedDistrict({ commit, dispatch }, districtId) {
      commit('SET_SELECTED_DISTRICT', districtId)
      // Refetch buildings when district changes
      return dispatch('fetchBuildingsWithCalendars')
    },
    async fetchBuildingsWithCalendars({ commit, dispatch, state }) {
      commit('SET_LOADING_BUILDINGS', true)
      try {
        // Pass districtId to API - backend handles all filtering
        const response = await getBuildingsWithCalendars(state.selectedDistrictId)
        if (response.data && Array.isArray(response.data)) {
          // Backend returns only buildings with calendars, filtered by district if selected
          const buildings = response.data.map((building, index) => ({
            ...building,
            enabled: true,
            color: getColorForIndex(index),
          }))

          commit('SET_BUILDINGS', buildings)

          // Auto-fetch events for enabled buildings
          const enabledBuildingIds = buildings.map((b) => b.id)
          if (enabledBuildingIds.length > 0) {
            await dispatch('fetchCalendarEvents', enabledBuildingIds)
          }

          return buildings
        }
      } catch (error) {
        console.error('Failed to fetch buildings with calendars:', error)
        commit(
          'notification/NEW',
          {
            type: 'error',
            message: 'Error',
            description: 'Failed to load building calendars. Please try again.',
          },
          { root: true },
        )
        throw error
      } finally {
        commit('SET_LOADING_BUILDINGS', false)
      }
    },

    async fetchCalendarEvents({ commit, state }, buildingIds = null) {
      const targetBuildingIds =
        buildingIds ||
        state.buildings
          .filter((building) => building.enabled && building.hasCalendar)
          .map((building) => building.id)

      if (targetBuildingIds.length === 0) {
        commit('SET_EVENTS', [])
        return []
      }

      commit('SET_LOADING_EVENTS', true)
      try {
        const response = await getCalendarEvents(targetBuildingIds)
        if (response.data && Array.isArray(response.data)) {
          const events = response.data.map((event) => {
            const buildingColor = getBuildingColor(state.buildings, event.buildingId)
            const eventColor = event.color || buildingColor
            const building = state.buildings.find(b => b.id === event.buildingId)

            return {
              ...event,
              backgroundColor: eventColor,
              borderColor: eventColor,
              extendedProps: {
                ...event,
                building: building ? (building.buildingAbbr || building.buildingName) : (event.source === 'manual' ? 'Personal' : 'Unknown Building'),
                buildingName: building?.buildingName || (event.source === 'manual' ? 'Personal Event' : 'Unknown Building')
              }
            }
          })

          commit('SET_EVENTS', events)
          return events
        }
      } catch (error) {
        console.error('Failed to fetch calendar events:', error)
        commit(
          'notification/NEW',
          {
            type: 'error',
            message: 'Error',
            description: 'Failed to load calendar events. Please try again.',
          },
          { root: true },
        )
        throw error
      } finally {
        commit('SET_LOADING_EVENTS', false)
      }
    },

    setBuildingEnabled({ commit, dispatch }, { buildingId, enabled }) {
      commit('SET_BUILDING_ENABLED', { buildingId, enabled })
      return dispatch('fetchCalendarEvents')
    },

    async createEvent({ commit, dispatch }, eventData) {
      const response = await createCalendarEvent(eventData)
      if (response.data) {
        await dispatch('fetchCalendarEvents')
      }
      return response.data
    },

    async deleteEvent({ dispatch }, eventId) {
      const response = await deleteCalendarEvent(eventId)
      if (response.data?.success) {
        await dispatch('fetchCalendarEvents')
      }
      return response.data
    },
  },
  getters: {
    enabledBuildings: (state) => {
      return state.buildings.filter((building) => building.enabled)
    },

    filteredBuildings: (state) => (filter, searchQuery) => {
      let filtered = state.buildings

      // Filter by dropdown selection
      if (filter === 'enabled') {
        filtered = filtered.filter((building) => building.enabled)
      } else if (filter === 'disabled') {
        filtered = filtered.filter((building) => !building.enabled)
      }

      // Filter by search query (search both name and abbreviation)
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        filtered = filtered.filter(
          (building) =>
            building.buildingName.toLowerCase().includes(query) ||
            (building.buildingAbbr &&
              building.buildingAbbr.toLowerCase().includes(query)),
        )
      }

      return filtered
    },
    filteredEvents: (state) => {
      return state.events.filter((event) => {
        if (event.source === 'manual') return true
        const building = state.buildings.find((b) => b.id === event.buildingId)
        return building && building.enabled
      })
    },
  },
}

// Helper functions
function getColorForIndex(index) {
  return CALENDAR_COLORS[index % CALENDAR_COLORS.length]
}

function getBuildingColor(buildings, buildingId) {
  return buildings.find(b => b.id === buildingId)?.color || CALENDAR_COLORS[0]
}
